import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import {
  User, Mail, Phone, MapPin, Building, Shield, Save, 
  Upload, CheckCircle, AlertCircle, Trash2, FileText, Globe,Eye
} from 'lucide-react';
import { toast } from 'react-toastify';

const SellerProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    address: '',
    companyName: '',
    companyDescription: '',
    businessType: '',
    GSTNumber: '',
    licenseNumber: '',
    port: '',
    loadingPort: '',
    destinationPort: ''
  });

  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [licenseImage, setLicenseImage] = useState(null);
  const [licenseImageUrl, setLicenseImageUrl] = useState('');
  const [profilePercentage, setProfilePercentage] = useState(0);
  const [charCountDesc, setCharCountDesc] = useState(0);

  const businessTypes = [
    'Manufacturer',
    'Exporter',
    'Wholesaler',
    'Distributor',
    'Trader',
    'Supplier',
    'Service Provider',
    'Consultant',
    'Agent'
  ];

  const countries = [
    { value: 'US', label: 'United States' },
    { value: 'IN', label: 'India' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'SG', label: 'Singapore' },
    { value: 'CN', label: 'China' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' },
    { value: 'KR', label: 'South Korea' }
  ];

  // Calculate profile percentage based on signup fields
  const calculateProfilePercentage = (data) => {
    const requiredFields = [
      'name', 'email', 'phone', 'country', 'state', 'city', 
      'postalCode', 'address', 'companyName', 'companyDescription', 
      'businessType'
    ];
    
    const filledFields = requiredFields.filter(field => 
      data[field] && data[field].toString().trim() !== ''
    ).length;
    
    return Math.round((filledFields / requiredFields.length) * 100);
  };

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      if (user?.uid) {
        try {
          setLoading(true);
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const loadedFormData = {
              name: data.name || '',
              email: data.email || '',
              phone: data.phone || '',
              country: data.country || '',
              state: data.state || '',
              city: data.city || '',
              postalCode: data.postalCode || '',
              address: data.address || '',
              companyName: data.companyName || '',
              companyDescription: data.companyDescription || '',
              businessType: data.businessType || '',
              GSTNumber: data.GSTNumber || '',
              licenseNumber: data.licenseNumber || '',
              port: data.port || '',
              loadingPort: data.loadingPort || '',
              destinationPort: data.destinationPort || ''
            };
            
            setFormData(loadedFormData);
            setCharCountDesc(loadedFormData.companyDescription?.length || 0);

            // Set image URLs if they exist
            if (data.companyLogoUrl) setCompanyLogoUrl(data.companyLogoUrl);
            if (data.licenseImageUrl) setLicenseImageUrl(data.licenseImageUrl);

            // Calculate profile percentage
            const percentage = calculateProfilePercentage(loadedFormData);
            setProfilePercentage(percentage || 0);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          toast.error('Failed to load profile data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Update character count for description
    if (name === 'companyDescription') {
      setCharCountDesc(value.length);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file || !user?.uid) return null;
    
    try {
      setUploading(true);
      const fileRef = ref(storage, `users/${user.uid}/${type}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setUploading(false);
      return downloadURL;
    } catch (error) {
      setUploading(false);
      console.error('File upload error:', error);
      toast.error(`Failed to upload ${type}`);
      return null;
    }
  };

  const handleRemoveFile = async (type) => {
    try {
      if (type === 'logo' && companyLogoUrl) {
        const fileRef = ref(storage, companyLogoUrl);
        await deleteObject(fileRef);
        setCompanyLogoUrl('');
        setCompanyLogo(null);
        toast.success('Company logo removed');
      } else if (type === 'license' && licenseImageUrl) {
        const fileRef = ref(storage, licenseImageUrl);
        await deleteObject(fileRef);
        setLicenseImageUrl('');
        setLicenseImage(null);
        toast.success('License document removed');
      }
    } catch (error) {
      console.error('File removal error:', error);
      // Even if deletion fails, clear from state
      if (type === 'logo') {
        setCompanyLogoUrl('');
        setCompanyLogo(null);
      } else if (type === 'license') {
        setLicenseImageUrl('');
        setLicenseImage(null);
      }
      toast.success('File removed from profile');
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validDocumentTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const validTypes = type === 'logo' ? validImageTypes : [...validImageTypes, ...validDocumentTypes];
    
    const maxSize = type === 'logo' ? 2 * 1024 * 1024 : 5 * 1024 * 1024; // 2MB for logo, 5MB for license
    
    if (!validTypes.includes(file.type)) {
      toast.error(`Please upload a valid file (${type === 'logo' ? 'JPG, PNG, WEBP' : 'JPG, PNG, WEBP, PDF'})`);
      return;
    }
    
    if (file.size > maxSize) {
      toast.error(`File size should be less than ${maxSize / 1024 / 1024}MB`);
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    
    if (type === 'logo') {
      setCompanyLogo(file);
      setCompanyLogoUrl(previewUrl);
    } else if (type === 'license') {
      setLicenseImage(file);
      setLicenseImageUrl(previewUrl);
    }

    e.target.value = null; // Reset input
  };

  const viewFile = (type) => {
    const url = type === 'logo' ? companyLogoUrl : licenseImageUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const validateForm = () => {
    const errors = [];
    
    // Check required fields (same as signup)
    const requiredFields = [
      { field: 'name', label: 'Contact Person Name' },
      { field: 'companyName', label: 'Company Name' },
      { field: 'businessType', label: 'Business Type' },
      { field: 'companyDescription', label: 'Company Description' },
      { field: 'address', label: 'Address' },
      { field: 'country', label: 'Country' },
      { field: 'state', label: 'State/Province' },
      { field: 'city', label: 'City' },
      { field: 'postalCode', label: 'Postal Code' },
      { field: 'phone', label: 'Phone Number' }
    ];

    requiredFields.forEach(({ field, label }) => {
      if (!formData[field]?.toString().trim()) {
        errors.push(`${label} is required`);
      }
    });

    // Validate company description length
    if (formData.companyDescription && formData.companyDescription.length < 50) {
      errors.push('Company description should be at least 50 characters');
    }

    if (errors.length > 0) {
      toast.error(errors[0]);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);

    try {
      let companyLogoUrlToSave = companyLogoUrl;
      let licenseImageUrlToSave = licenseImageUrl;

      // Upload new files if selected
      if (companyLogo) {
        const uploadedUrl = await handleFileUpload(companyLogo, 'companyLogo');
        if (uploadedUrl) {
          companyLogoUrlToSave = uploadedUrl;
          // Revoke old preview URL if it was a blob URL
          if (companyLogoUrl && companyLogoUrl.startsWith('blob:')) {
            URL.revokeObjectURL(companyLogoUrl);
          }
          setCompanyLogoUrl(uploadedUrl);
        }
      }
      
      if (licenseImage) {
        const uploadedUrl = await handleFileUpload(licenseImage, 'license');
        if (uploadedUrl) {
          licenseImageUrlToSave = uploadedUrl;
          // Revoke old preview URL if it was a blob URL
          if (licenseImageUrl && licenseImageUrl.startsWith('blob:')) {
            URL.revokeObjectURL(licenseImageUrl);
          }
          setLicenseImageUrl(uploadedUrl);
        }
      }

      // Calculate profile percentage
      const newProfilePercentage = calculateProfilePercentage(formData);

      // Prepare update data - ONLY fields from signup
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        postalCode: formData.postalCode,
        address: formData.address,
        companyName: formData.companyName,
        companyDescription: formData.companyDescription,
        businessType: formData.businessType,
        GSTNumber: formData.GSTNumber,
        licenseNumber: formData.licenseNumber,
        port: formData.port,
        loadingPort: formData.loadingPort,
        destinationPort: formData.destinationPort,
        updatedAt: serverTimestamp(),
        profilePercentage: newProfilePercentage || 0
      };

      // Add optional fields only if they have values
      if (companyLogoUrlToSave) {
        updateData.companyLogoUrl = companyLogoUrlToSave;
      }
      
      if (licenseImageUrlToSave) {
        updateData.licenseImageUrl = licenseImageUrlToSave;
      }

      // Update Firestore
      await updateDoc(doc(db, 'users', user.uid), updateData);

      // Clear file inputs
      setCompanyLogo(null);
      setLicenseImage(null);

      // Update local state
      setProfilePercentage(newProfilePercentage || 0);
      setIsEditing(false);
      
      toast.success('Profile updated successfully!');

    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reload original data
    if (user?.uid) {
      getDoc(doc(db, 'users', user.uid)).then(doc => {
        if (doc.exists()) {
          const data = doc.data();
          setFormData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            country: data.country || '',
            state: data.state || '',
            city: data.city || '',
            postalCode: data.postalCode || '',
            address: data.address || '',
            companyName: data.companyName || '',
            companyDescription: data.companyDescription || '',
            businessType: data.businessType || '',
            GSTNumber: data.GSTNumber || '',
            licenseNumber: data.licenseNumber || '',
            port: data.port || '',
            loadingPort: data.loadingPort || '',
            destinationPort: data.destinationPort || ''
          });
          setCharCountDesc(data.companyDescription?.length || 0);
          if (data.companyLogoUrl) setCompanyLogoUrl(data.companyLogoUrl);
          if (data.licenseImageUrl) setLicenseImageUrl(data.licenseImageUrl);
          
          // Recalculate profile percentage
          const percentage = calculateProfilePercentage(data);
          setProfilePercentage(percentage || 0);
        }
      });
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-10 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="h-12 w-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-10 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-4 text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Seller Profile</h1>
              <p className="text-gray-600 mt-1">Manage your business profile and settings</p>
            </div>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 sm:px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-md transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile Completion */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-800">Profile Completion</h3>
              <p className="text-sm text-gray-600 mt-1">
                Complete your profile to increase trust and visibility
              </p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-orange-600">{profilePercentage}%</span>
              <p className="text-xs text-gray-600 mt-1">Complete</p>
            </div>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${profilePercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Main Profile Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {isEditing ? (
              <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800">Editing Mode</h2>
                <p className="text-sm text-gray-600">Update your business information below</p>
              </div>
            ) : null}

            <div className="p-6">
              {/* Personal Information */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-orange-500" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  {/* Contact Person Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Contact Person Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="Enter your full name"
                        required
                      />
                      <User className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Email Address (Non-editable) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-gray-50 text-sm sm:text-base cursor-not-allowed"
                      />
                      <Mail className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Email cannot be changed. Contact support for assistance.
                    </p>
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="+1 (555) 123-4567"
                        required
                      />
                      <Phone className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Building className="h-5 w-5 mr-2 text-orange-500" />
                  Company Information
                </h3>
                
                {/* Company Name & Business Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Company Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                        placeholder="Enter company name"
                        required
                      />
                      <Building className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Business Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Business Type
                    </label>
                    <div className="relative">
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 appearance-none text-sm sm:text-base disabled:bg-gray-50"
                        required
                      >
                        <option value="">Select Business Type</option>
                        {businessTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 sm:px-3 text-gray-500">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="text-red-500">*</span> Company Description
                  </label>
                  <div className="relative">
                    <textarea
                      name="companyDescription"
                      value={formData.companyDescription}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      rows="4"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 resize-none text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="Describe your company, products, and services..."
                      minLength="50"
                      required
                    />
                    <div className="absolute bottom-3 right-3 text-xs sm:text-sm text-gray-500">
                      {charCountDesc} characters
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum 50 characters recommended for better visibility
                  </p>
                </div>

                {/* Legal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GST/VAT Number
                    </label>
                    <input
                      type="text"
                      name="GSTNumber"
                      value={formData.GSTNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="Enter GST/VAT number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business License Number
                    </label>
                    <input
                      type="text"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="Enter license number"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-orange-500" />
                  Address Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Country
                    </label>
                    <div className="relative">
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 appearance-none text-sm sm:text-base disabled:bg-gray-50"
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 sm:px-3 text-gray-500">
                        <Globe className="h-4 w-4" />
                      </div>
                    </div>
                  </div>

                  {/* State/Province */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> State/Province
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      required
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      required
                    />
                  </div>

                  {/* Postal Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      required
                    />
                  </div>

                  {/* Full Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="text-red-500">*</span> Full Address
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      rows="3"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Port Information */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Port Information (Optional)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Main Port
                    </label>
                    <input
                      type="text"
                      name="port"
                      value={formData.port}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="e.g., Port of Shanghai"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loading Port
                    </label>
                    <input
                      type="text"
                      name="loadingPort"
                      value={formData.loadingPort}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="e.g., Port of Ningbo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destination Port
                    </label>
                    <input
                      type="text"
                      name="destinationPort"
                      value={formData.destinationPort}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm sm:text-base disabled:bg-gray-50"
                      placeholder="e.g., Port of Los Angeles"
                    />
                  </div>
                </div>
              </div>

              {/* Document Uploads */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Document Uploads</h3>
                
                <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
                  {/* Left side - Document placeholders */}
                  <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:gap-6">
                    {/* Company Logo */}
                    <div className="w-full sm:w-auto">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Logo
                        </label>
                        <div 
                          className={`w-full sm:w-48 h-48 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${
                            companyLogoUrl 
                              ? 'border-2 border-gray-300 bg-gray-50 overflow-hidden' 
                              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-orange-400'
                          }`}
                          onClick={() => companyLogoUrl && viewFile('logo')}
                        >
                          {companyLogoUrl ? (
                            <>
                              <img
                                src={companyLogoUrl}
                                alt="Company Logo"
                                className="w-full h-full object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/400x400?text=Image+Error';
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all rounded-lg" />
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col items-center p-4">
                                <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" />
                                <span className="text-gray-500 text-sm text-center">Company Logo</span>
                                <span className="text-gray-400 text-xs mt-1 text-center">Click to upload</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Action buttons below */}
                        <div className="flex gap-2 mt-3 justify-center flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              document.getElementById('file-logo').click();
                            }}
                            className={`p-2 transition-colors rounded-md ${
                              companyLogoUrl
                                ? 'text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100'
                                : 'text-gray-600 hover:text-orange-600 bg-gray-100 hover:bg-gray-200'
                            }`}
                            title={companyLogoUrl ? "Replace Logo" : "Upload Logo"}
                            disabled={!isEditing || uploading}
                          >
                            <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                          
                          {companyLogoUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewFile('logo');
                              }}
                              className="p-2 text-gray-600 hover:text-green-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md"
                              title="View Logo"
                              disabled={!isEditing}
                            >
                              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          )}
                          
                          {companyLogoUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to remove company logo?')) {
                                  handleRemoveFile('logo');
                                }
                              }}
                              className="p-2 text-gray-600 hover:text-red-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md"
                              title="Remove Logo"
                              disabled={!isEditing || uploading}
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          )}
                          
                          <input
                            id="file-logo"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={(e) => handleFileSelect(e, 'logo')}
                            className="hidden"
                            disabled={!isEditing || uploading}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business License */}
                    <div className="w-full sm:w-auto">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Business License
                        </label>
                        <div 
                          className={`w-full sm:w-48 h-48 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${
                            licenseImageUrl 
                              ? 'border-2 border-gray-300 bg-gray-50 overflow-hidden' 
                              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-orange-400'
                          }`}
                          onClick={() => licenseImageUrl && viewFile('license')}
                        >
                          {licenseImageUrl ? (
                            <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                              <FileText className="h-12 w-12 text-gray-400" />
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all rounded-lg" />
                            </div>
                          ) : (
                            <>
                              <div className="flex flex-col items-center p-4">
                                <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" />
                                <span className="text-gray-500 text-sm text-center">Business License</span>
                                <span className="text-gray-400 text-xs mt-1 text-center">Click to upload</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Action buttons below */}
                        <div className="flex gap-2 mt-3 justify-center flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              document.getElementById('file-license').click();
                            }}
                            className={`p-2 transition-colors rounded-md ${
                              licenseImageUrl
                                ? 'text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100'
                                : 'text-gray-600 hover:text-orange-600 bg-gray-100 hover:bg-gray-200'
                            }`}
                            title={licenseImageUrl ? "Replace License" : "Upload License"}
                            disabled={!isEditing || uploading}
                          >
                            <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                          
                          {licenseImageUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewFile('license');
                              }}
                              className="p-2 text-gray-600 hover:text-green-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md"
                              title="View License"
                              disabled={!isEditing}
                            >
                              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          )}
                          
                          {licenseImageUrl && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to remove business license?')) {
                                  handleRemoveFile('license');
                                }
                              }}
                              className="p-2 text-gray-600 hover:text-red-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md"
                              title="Remove License"
                              disabled={!isEditing || uploading}
                            >
                              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          )}
                          
                          <input
                            id="file-license"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,.pdf"
                            onChange={(e) => handleFileSelect(e, 'license')}
                            className="hidden"
                            disabled={!isEditing || uploading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Tips */}
                  <div className="flex-1">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <h3 className="text-sm font-semibold text-blue-900 mb-3">Document Guidelines</h3>
                          <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Company Logo: JPG, PNG, WebP (Max 2MB)</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Business License: PDF, JPG, PNG (Max 5MB)</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Make sure documents are clear and readable</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Documents help verify your business and build trust</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {isEditing && (
                <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 sm:px-8 py-2.5 border border-gray-300 bg-white text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 order-2 sm:order-1"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 sm:px-8 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px] order-1 sm:order-2"
                    disabled={saving || uploading}
                  >
                    {saving ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Verification Status */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Verification Status</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-4 ${user?.isVerified ? 'bg-green-100' : 'bg-yellow-100'}`}>
                {user?.isVerified ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Business Verification</p>
                <p className="text-sm text-gray-600">
                  {user?.isVerified ? 'Your business is verified and trusted' : 'Pending verification review'}
                </p>
              </div>
            </div>
            <span className={`px-4 py-2 text-sm font-semibold rounded-full ${user?.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {user?.isVerified ? 'Verified' : 'In Review'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerProfile;